import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Screenshot } from '../../components/DocsComponents'

const WorkforceSchedulingPayroll = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title2">Rostering, availability, payroll, and leave</Typography>
      <Typography>
        AmbuNet supports a joined-up workforce workflow from planning shifts
        through to staff acknowledgement, leave visibility, shift application
        review, pay runs, pending pay estimates, and staff-facing pay
        summaries. This guide explains how the pieces fit together for managers
        and staff.
      </Typography>
      <Alert severity="info" variant="outlined">
        AmbuNet records pay-run and remittance information for operational
        control. It is not a statutory payroll engine and does not calculate tax,
        National Insurance, pension deductions, or HMRC submissions.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Sidebar structure</Typography>
      <Typography>
        Staff self-service tools sit under My Work: Available Shifts, My
        Shifts, My Availability, and My Pay — visible to everyone. Each
        Workforce entry has its own access rule: Shift Applications is shown
        to the Personnel and Event Planning teams, Global Roster to Personnel,
        Staff Availability to Personnel and Manager, and Overtime Claims and
        Payroll to Personnel (on Premium and above). PTS Co-Ordinator access
        on its own unlocks none of the Workforce tools. The People section
        (employee records, subcontractors, applications, training, and DBS
        checks) requires the Personnel team.
      </Typography>
      <Screenshot url="workforce/sidebar-workforce.png">
        The sidebar My Work and Workforce sections, with the People section
        below.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">1. Plan work using rosters and shifts</Typography>
      <Typography>
        Managers can use <DocLink url="/global-roster" text="Global Roster" />
        to create named draft rota plans, add roster shifts, assign staff, and
        view event, PTS, frontline, and roster work in one place. Rota plans are
        planning containers rather than date-bound periods, so planners choose
        the draft plan they are working on and build shifts into it.
      </Typography>
      <Typography>
        The Selected roster control chooses the draft rota plan currently being
        viewed or edited. Draft shifts for the selected plan appear in teal with
        a pulsing highlight, while draft shifts from other plans are hidden.
        Published rota-plan shifts move into the normal shift workflow, while
        the published plan is kept for history rather than day-to-day selection.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Archiving a roster removes <strong>any</strong> roster that is not
        Locked — published ones included — and{' '}
        <strong>deletes its unverified, unpaid shifts</strong> in the process.
        A roster containing verified or paid shifts cannot be archived at all.
        Treat archive as "throw this plan and its unworked shifts away", not as
        tidying.
      </Alert>
      <Screenshot url="workforce/global-roster.png">
        Global Roster with the rota plan selector, New Rota Plan, Add Shift,
        Publish, and archive controls above the week grid.
      </Screenshot>
      <Typography>
        New rota-plan shifts can be repeated across selected weekdays until a
        chosen end date. For example, a manager can create the same shift every
        Tuesday, Thursday, and Saturday, with the form showing the number of
        shifts that will be created before saving.
      </Typography>
      <Typography>
        Existing event, PTS, frontline, or roster shifts can be opened and
        copied into the currently selected draft rota plan. This opens a new
        rota-plan shift form pre-filled from the original shift and linked to
        the selected plan.
      </Typography>
      <Typography>
        Assignment checks warn managers when a member of staff has an overlapping
        shift, approved leave, role mismatch, inactive employment status,
        training issue, or DBS issue where those checks are enabled.
      </Typography>
      <Typography>
        Event, PTS, and frontline shifts shown in Global Roster can be edited
        directly from the roster's shift drawer — time, role, pay, notes, and
        bookability — without leaving the page. A single vacant or assigned
        shift can also have its assignment changed here. Identical vacant
        shifts collapse into one card, and editing that card updates all of
        them at once; the drawer shows how many and disables assignment in
        that case, since assigning one specific person still needs the
        source module. Source buttons, such as Open Event or Open PTS Duty,
        link back to the shift's original record for full context, and
        shifts can also be copied into the current draft rota plan if they
        should be used as a planning template.
      </Typography>
      <Screenshot url="workforce/shift-drawer.png">
        The shift details drawer with editable time, role, pay, and assignment
        fields.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">2. Publish and acknowledge assigned work</Typography>
      <Typography>
        Publishing a roster marks assigned roster shifts as requiring
        acknowledgement and notifies staff. Staff review those shifts from{' '}
        <DocLink url="/my-shifts" text="My Shifts" /> and can acknowledge that
        they have seen the shift or mark that they cannot attend.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Declining a published assigned shift does not remove the staff member
        from the shift. It flags the issue for managers while preserving the
        original assignment for audit.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">3. Keep available shifts open for applications</Typography>
      <Typography>
        Event and PTS shifts can still be advertised through{' '}
        <DocLink url="/available-shifts" text="Available Shifts" />. Staff can
        apply for available work, and managers can review applications using the
        normal shift application process. This sits alongside assigned roster
        work rather than replacing it.
      </Typography>
      <Typography>
        Advertising several people for the same role, date, and time creates
        one listing for staff rather than a separate row per vacancy. The
        listing shows how many shifts are still available, and applicants are
        spread automatically across the underlying vacancies. Managers review
        every applicant for a vacancy together from{' '}
        <DocLink url="/shift-applications" text="Shift Applications" />,
        accept up to the number of remaining slots in one action, then reject
        everyone else with a single Reject Remaining Applicants action.
      </Typography>
      <Typography>
        Global Roster now includes pending shift applications for the selected
        date range. Identical vacant shifts on the same day collapse into a
        single card with a count badge, and an icon marks any vacant shift
        with pending applications. Managers can review applications directly
        from the roster.
      </Typography>
      <Screenshot url="workforce/shift-applications.png">
        Pending applications for a vacant shift, reviewed directly from the
        shift drawer.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">4. Record availability and annual leave</Typography>
      <Typography>
        Staff submit leave, sickness, and unavailable periods from{' '}
        <DocLink url="/my-availability" text="My Availability" />, and can
        also record when they are <strong>available to work</strong> so
        planners know who is free. Managers review requests from{' '}
        <DocLink url="/employees/availability" text="Staff Availability" />.
        Approved leave and unavailable periods are used by assignment checks
        so managers can avoid assigning staff who cannot work; an available
        window never blocks an assignment.
      </Typography>
      <Typography>
        Availability and sickness are recorded straight away; leave and
        unavailable periods wait for a manager. Requests are checked before
        they are saved: dates must be valid and in a sensible range, a record
        cannot overlap an existing pending or approved record for the same
        person (leave may be recorded over a declared available window, but
        nothing can be stacked on top of leave), managers cannot approve their
        own requests, and staff can only cancel a period that has not yet
        ended.
      </Typography>
      <Typography>
        On <DocLink url="/global-roster" text="Global Roster" /> in the Week
        view with Display set to Staff, each person's availability appears as
        a band above their shifts: green for available windows, hatched red
        for approved leave and unavailable periods, and hatched amber for
        requests still awaiting a decision. Staff with availability but no
        shifts get a row too, and the <strong>Available staff only</strong>{' '}
        toggle narrows the rows to people who have said they can work that
        week. With Display set to Source, Role or Rota Plan, an Available
        staff row at the bottom of the week grid counts who can work each
        day instead. The Month view shows the same count at the bottom of
        each day, and in the Year view a green ring around a day means at
        least one person is available; hover the day to see who. The Year
        view's Display offers Coverage (the status heatmap) or Source, which
        colours each day by where its shifts come from. The Year view opens
        on the next 12 months starting with the current month; the arrows
        then page through calendar years, and a Next 12 months button brings
        the rolling view back.
      </Typography>
      <Typography>
        In the Week view, Display: Source gives every event its own row and
        splits patient transport by organisation: duties dedicated to one
        organisation (such as a hospital discharge duty) get that
        organisation's row, and everything else sits under Multiple
        organisations. The Filter menu's Events Only and PTS Only options
        narrow any view to just that kind of work.
      </Typography>
      <Typography>
        Patient transport duties that have no crew shifts at all appear in
        the Week view as a dashed red row directly under Vacant Shifts, so
        work nobody has been put on yet is never out of sight. Clicking one
        opens that duty on the PTS planning page for its date.
      </Typography>
      <Typography>
        The My Availability page also shows holiday balance information,
        including entitlement, accrued leave, approved annual leave, pending
        requests, remaining leave, and projected remaining leave if pending
        requests are approved.
      </Typography>
      <Screenshot url="workforce/my-availability.png">
        The My Availability holiday balance panel with entitlement, accrual,
        and remaining-days tiles.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">5. Maintain employee payroll settings</Typography>
      <Typography>
        Personnel users manage employee payroll profiles from the employee
        record Payroll tab. This includes employment type, contracted hours per
        week, annual leave entitlement, leave year start month, working hours per
        day for leave calculations, and BACS details.
      </Typography>
      <Typography>
        Employment type is used to categorise pay-run totals as PAYE-style,
        invoice/remittance, or unknown. Annual leave fields drive the holiday
        balance shown on My Availability.
      </Typography>
      <Screenshot url="workforce/employee-payroll.png">
        The Payroll tab on an employee record with employment type, holiday
        entitlement, and BACS details.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">6. Review overtime before payroll</Typography>
      <Typography>
        When a shift runs late and overtime is claimed, managers review it from{' '}
        <DocLink url="/overtime-claims" text="Overtime Claims" />. Approved
        overtime is included in payroll calculations using the approved rate and
        approved minutes. Pending or rejected overtime is not included in a
        pay-run total.
      </Typography>
      <Typography>
        An approved or rejected claim can be undone from the claim drawer,
        returning it to Pending for re-review — but not once the shift has
        been marked paid or included in a pay run, since approved overtime
        feeds pay-run snapshots. Undo the payment or cancel the draft pay run
        first.
      </Typography>
      <Typography>
        If a shift's assignee changes — removed, or swapped for someone else —
        any overtime claim on the shift is dropped and the removal is logged,
        since the claim belonged to the person who worked late. A reassignment
        is refused outright while the claim is paid or in a pay run.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">7. Create and pay a pay run</Typography>
      <Typography>
        Personnel users use <DocLink url="/employees/payroll" text="Payroll" />
        to search verified, unpaid shifts and create a pay run. Creating a pay
        run snapshots each line, including worker type, shift details, hours,
        base pay, fixed bonuses, approved overtime, and total pay. Fixed
        bonuses are per-shift sums (a label and an amount, e.g. "Vehicle
        pickup £20") added on the shift itself and paid on top of the rate;
        they appear on pay-run lines and My Pay but never change the
        overtime rate. A shift can also carry <strong>paid travel time</strong>{' '}
        — minutes before and after the on-site window at a separate hourly
        rate (pre-filled from the default travel rate in Configuration &gt;
        Shifts) — which is paid on top without moving the shift's start or
        end, so clocking and overtime are unaffected.
      </Typography>
      <Typography>
        Shifts must be verified before they can enter a pay run, and
        verification locks once a shift is paid or in a run. The Payroll
        page's <strong>Missing payment type</strong> fixer lists workers with
        no employment type set so each can be assigned one in place rather
        than landing in the "unknown" total. Approved staff{' '}
        <strong>expense claims</strong> — logged by staff from My Pay, and
        approved or rejected by Personnel from Payroll — join the run as their
        own expense lines on top of shift pay.
      </Typography>
      <Typography>
        Draft pay runs lock their selected shifts until the run is marked paid
        or cancelled. Marking a pay run as paid updates the underlying shifts and
        makes the statement visible to staff in My Pay — but it is blocked
        while any staff invoice covering the run's shifts is still Submitted
        or Queried: approve or reject those invoices first. Individual shifts
        paid through a pay run cannot be individually un-marked as paid
        afterwards. A paid run can be exported as a <strong>CSV</strong> of
        its lines (including expenses and VAT paid) for your accountant.
      </Typography>
      <Typography>
        Invoice workers can upload staff pay invoices against completed unpaid
        shifts from My Pay. Personnel users can also upload invoices received by
        email from the Staff Invoices panel, then approve, query, or reject them
        before payroll is finalised. The invoice amount must match the total of
        the selected lines: each shift's base pay, bonuses and travel, plus any
        approved overtime ticked alongside it — plus standard-rate VAT on top
        for workers with a VAT number on their payroll profile. A shift
        already on a submitted, approved, queried or matched invoice cannot be
        selected again, and overtime still awaiting a decision cannot be
        invoiced until it is approved. Approved staff invoices are matched when
        the linked shifts are paid through a pay run, and the VAT paid to
        registered workers is snapshotted onto the run.
      </Typography>
      <Screenshot url="workforce/staff-invoices.png">
        Reviewing a submitted staff invoice with the Reject, Query, and
        Approve actions.
      </Screenshot>
      <Screenshot url="workforce/pay-runs.png">
        The Payroll page with wage totals and the Pay Runs panel.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">8. Staff view pay summaries and pending pay</Typography>
      <Typography>
        Staff use <DocLink url="/my-pay" text="My Pay" /> to see paid pay-run
        lines that belong to them. PAYE-style workers see Pay Statements.
        Invoice-style workers see Remittance Summaries. Staff can open the
        detail for a run and download a CSV copy for their own records.
      </Typography>
      <Typography>
        My Pay also includes Pending Pay for completed unpaid shifts. This is an
        estimate only and stays separate from paid statements. Pending Pay shows
        whether a shift is awaiting verification, ready for payroll, or already
        included in a draft pay run.
      </Typography>
      <Typography>
        Invoice workers also see Uploaded Invoices on My Pay. They can submit a
        PDF or image of their invoice, select which completed unpaid shifts it
        covers, and track whether the invoice is submitted, approved, queried,
        rejected, or matched to a paid run. Pending Pay highlights shifts with
        an overtime claim still awaiting a decision, and the shift picker lists
        each overtime claim on its own row, so approved overtime can be
        invoiced with its shift or on a later invoice once it has been decided.
      </Typography>
      <Typography>
        Instead of uploading their own document, an invoice worker can have
        AmbuNet <strong>generate a self-billing invoice PDF</strong> from My
        Pay: they pick the shifts and approved overtime it covers and the PDF
        is produced with the correct lines and totals — adding standard-rate
        VAT for VAT-registered workers — and submitted for review like any
        other staff invoice. Staff also log <strong>expense claims</strong>{' '}
        from My Pay and track their status there.
      </Typography>
      <Screenshot url="workforce/my-pay-upload-invoice.png">
        The upload invoice dialog for an invoice worker, with eligible shifts
        selected.
      </Screenshot>
      <Screenshot url="workforce/my-pay.png">
        My Pay showing summary tiles, Pending Pay lines, and uploaded
        invoices.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Recommended operating rhythm</Typography>
      <Typography>
        Build or update the roster, check leave conflicts, publish assignments,
        review acknowledgements and declines, review shift applications in the
        roster, approve overtime after shifts are completed, verify shifts,
        create the pay run, then mark the run paid. Staff can then check My
        Availability, Pending Pay, and paid statements without needing manager
        support for basic questions.
      </Typography>
    </Stack>
  </Stack>
)

export default WorkforceSchedulingPayroll
